import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { BarcodeScanner } from '@/components/ui/BarcodeScanner'
import { Button } from '@/components/ui/button'
import { GenreMultiSelect } from '@/components/ui/GenreMultiSelect'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/Select'
import { useCheckISBNExists } from '@/hooks/books'
import { useISBN } from '@/hooks/isbn'
import { captureError, trackEvent } from '@/lib/monitoring'
import { todayISO } from '@/lib/utils'
import { isValidISBN } from '@/services/isbn'
import {
  BookOwnershipEnum,
  BookStatusEnum,
  BookTypeEnum,
  type CreateBook,
  CreateBookSchema,
} from '@/types/book'

// Form-specific schema with required status field - genres handled separately
const BookFormSchema = CreateBookSchema.omit({ cover_url: true }).extend({
  status: BookStatusEnum,
  type: BookTypeEnum,
  ownership: BookOwnershipEnum,
  cover_url: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true
        try {
          new URL(val)
          return true
        } catch {
          return false
        }
      },
      { message: 'Must be a valid URL' }
    ),
})

type BookFormData = z.infer<typeof BookFormSchema>

interface BookFormProps {
  onSubmit: (data: CreateBook, genreIds: string[]) => Promise<void>
  onCancel?: () => void
  isLoading?: boolean
  initialData?: Partial<CreateBook> & { id?: string }
  initialGenres?: string[]
  hideStatusField?: boolean
}

export function BookForm({
  onSubmit,
  onCancel,
  isLoading = false,
  initialData,
  initialGenres = [],
  hideStatusField = false,
}: BookFormProps) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedGenres, setSelectedGenres] = useState<string[]>(initialGenres)
  const [isbnInput, setIsbnInput] = useState('')
  const [hasAutoFilled, setHasAutoFilled] = useState(false)
  const [isScannerOpen, setIsScannerOpen] = useState(false)
  const [isbnValidation, setIsbnValidation] = useState<{
    isDuplicate: boolean
    existingBookTitle?: string
    isChecking: boolean
  }>({ isDuplicate: false, isChecking: false })

  const {
    data: isbnData,
    isLoading: isIsbnLoading,
    error: isbnError,
    fetchBookData,
    reset: resetISBN,
  } = useISBN()

  const checkISBNExists = useCheckISBNExists()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
    control,
  } = useForm<BookFormData>({
    resolver: zodResolver(BookFormSchema),
    defaultValues: {
      title: initialData?.title || '',
      author: initialData?.author || '',
      isbn: initialData?.isbn || '',
      cover_url: initialData?.cover_url || '',
      status: initialData?.status || ('unread' as const),
      type: initialData?.type || ('physical' as const),
      ownership: initialData?.ownership || ('owned' as const),
      borrowed_from: initialData?.borrowed_from || '',
      rating: initialData?.rating || undefined,
      notes: initialData?.notes || '',
      start_date: initialData?.start_date || undefined,
      end_date: initialData?.end_date || undefined,
    },
  })

  // Watch the status field to conditionally show rating
  const currentStatus = watch('status')

  // Pre-fill reading dates with today when the status changes to one that needs them,
  // so yearly stats count the book. Editing a book without changing status keeps its dates.
  const statusChanged = currentStatus !== (initialData?.status ?? 'unread')
  useEffect(() => {
    if (!statusChanged) return
    if (currentStatus === 'read' && !getValues('end_date')) {
      setValue('end_date', todayISO())
    }
    if (currentStatus === 'reading' && !getValues('start_date')) {
      setValue('start_date', todayISO())
    }
  }, [currentStatus, statusChanged, getValues, setValue])

  // Reset form when initialData changes (for edit mode)
  // Only reset if initialData has an id (meaning it's editing an existing book)
  useEffect(() => {
    if (initialData && 'id' in initialData && initialData.id) {
      reset({
        title: initialData.title || '',
        author: initialData.author || '',
        isbn: initialData.isbn || '',
        cover_url: initialData.cover_url || '',
        status: initialData.status || 'unread',
        rating: initialData.rating || undefined,
        notes: initialData.notes || '',
        start_date: initialData.start_date || undefined,
        end_date: initialData.end_date || undefined,
      })
    }
  }, [initialData, reset])

  // Keep track of the previous initialGenres to avoid unnecessary updates
  const prevInitialGenresRef = useRef<string[]>()

  // Update selected genres when initialGenres changes
  useEffect(() => {
    const prevGenres = prevInitialGenresRef.current
    const currentGenres = initialGenres

    // Check if the arrays are actually different
    const areEqual =
      prevGenres &&
      prevGenres.length === currentGenres.length &&
      prevGenres.every((id, index) => id === currentGenres[index])

    if (!areEqual) {
      setSelectedGenres(currentGenres)
      prevInitialGenresRef.current = [...currentGenres]
    }
  }, [initialGenres])

  // Handle ISBN auto-fill
  useEffect(() => {
    if (isbnData && !hasAutoFilled) {
      // Auto-fill form fields with ISBN data
      setValue('title', isbnData.title)
      setValue('author', isbnData.authors.join(', '))
      setValue('isbn', isbnData.isbn)

      // Set cover image if available
      if (isbnData.imageUrl) {
        setValue('cover_url', isbnData.imageUrl)
      }

      setHasAutoFilled(true)
    }
  }, [isbnData, hasAutoFilled, setValue])

  // Reset auto-fill state when editing an existing book
  useEffect(() => {
    if (initialData && 'id' in initialData && initialData.id) {
      setHasAutoFilled(false)
      setIsbnInput(initialData.isbn || '')
    }
  }, [initialData])

  const handleISBNLookup = async () => {
    if (!isbnInput.trim()) return

    setHasAutoFilled(false)
    resetISBN()

    try {
      await fetchBookData(isbnInput.trim())
    } catch (error) {
      captureError(error as Error, { context: 'Failed to fetch book data' })
    }
  }

  const handleISBNInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setIsbnInput(value)

    // Reset auto-fill state when ISBN changes
    if (hasAutoFilled && value !== isbnData?.isbn) {
      setHasAutoFilled(false)
    }

    // Reset validation state
    setIsbnValidation({ isDuplicate: false, isChecking: false })

    // Check for duplicates if ISBN is valid and not in edit mode
    if (value.trim() && isValidISBN(value) && !initialData?.id) {
      setIsbnValidation((prev) => ({ ...prev, isChecking: true }))

      try {
        const result = await checkISBNExists.mutateAsync(value.trim())
        setIsbnValidation({
          isDuplicate: result.exists,
          existingBookTitle: result.existingBook?.title,
          isChecking: false,
        })
      } catch (_error) {
        setIsbnValidation({ isDuplicate: false, isChecking: false })
      }
    }
  }

  const handleBarcodeResult = async (code: string) => {
    setIsbnInput(code)
    setIsScannerOpen(false)

    // Reset validation state
    setIsbnValidation({ isDuplicate: false, isChecking: false })

    // Check for duplicates if not in edit mode
    if (isValidISBN(code) && !initialData?.id) {
      setIsbnValidation((prev) => ({ ...prev, isChecking: true }))

      try {
        const result = await checkISBNExists.mutateAsync(code.trim())
        setIsbnValidation({
          isDuplicate: result.exists,
          existingBookTitle: result.existingBook?.title,
          isChecking: false,
        })
      } catch (_error) {
        setIsbnValidation({ isDuplicate: false, isChecking: false })
      }
    }

    // Automatically trigger ISBN lookup after successful scan
    if (isValidISBN(code)) {
      setHasAutoFilled(false)
      resetISBN()

      try {
        await fetchBookData(code.trim())
      } catch (error) {
        captureError(error as Error, { context: 'Failed to fetch book data after barcode scan' })
      }
    }
  }

  const handleScannerError = (error: string) => {
    captureError(new Error(error), { context: 'Barcode scanner error' })
    setIsScannerOpen(false)
  }

  const canLookupISBN = isbnInput.trim() && isValidISBN(isbnInput) && !isIsbnLoading

  const handleFormSubmit = async (data: BookFormData) => {
    // Prevent submission if ISBN is duplicate and not in edit mode
    if (isbnValidation.isDuplicate && !initialData?.id) {
      return
    }

    try {
      setIsSubmitting(true)

      // Determine the method used for book creation
      let method: 'manual' | 'isbn' | 'barcode' = 'manual'
      if (hasAutoFilled && data.isbn) {
        method = isbnData ? 'isbn' : 'barcode'
      }

      // Track book creation event
      trackEvent('book_added', {
        method,
        genre_count: selectedGenres.length,
        has_cover: !!data.cover_url,
        status: data.status,
      })

      // Transform empty strings to null before submitting
      const submitData = {
        ...data,
        cover_url: data.cover_url && data.cover_url.trim() !== '' ? data.cover_url : null,
        start_date: data.start_date && data.start_date.trim() !== '' ? data.start_date : null,
        end_date: data.end_date && data.end_date.trim() !== '' ? data.end_date : null,
        borrowed_from:
          data.ownership === 'borrowed' && data.borrowed_from?.trim()
            ? data.borrowed_from.trim()
            : null,
      } as CreateBook

      // Status implies a date: reading started / finished today unless told otherwise
      if (statusChanged && submitData.status === 'read' && !submitData.end_date) {
        submitData.end_date = todayISO()
      }
      if (statusChanged && submitData.status === 'reading' && !submitData.start_date) {
        submitData.start_date = todayISO()
      }

      await onSubmit(submitData, selectedGenres)
      reset()
      setSelectedGenres([])
    } catch (error) {
      captureError(error as Error, { context: 'Error submitting book' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const isProcessing = isLoading || isSubmitting
  const canSubmit = !isProcessing && (!isbnValidation.isDuplicate || !!initialData?.id)

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* ISBN Lookup - First field for auto-fill */}
      <div className="space-y-2">
        <label htmlFor="isbn-lookup" className="text-sm font-medium text-foreground">
          {t('books.fields.isbn')} {t('common.optional')}
        </label>
        <div className="flex gap-2">
          <Input
            id="isbn-lookup"
            type="text"
            placeholder="978-0-123456-78-9 ou 0123456789"
            value={isbnInput}
            onChange={handleISBNInputChange}
            className={isbnError || isbnValidation.isDuplicate ? 'border-error' : ''}
          />
          <Button
            type="button"
            onClick={handleISBNLookup}
            disabled={!canLookupISBN}
            variant="outline"
            className="flex-shrink-0"
          >
            {isIsbnLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t('books.isbn.searching')}
              </div>
            ) : (
              t('books.isbn.search')
            )}
          </Button>
          <Button
            type="button"
            onClick={() => setIsScannerOpen(true)}
            disabled={isIsbnLoading}
            variant="outline"
            className="flex-shrink-0"
            title={t('books.scanner.scanBarcode')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </Button>
        </div>

        {isbnError && (
          <p className="text-sm text-error flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {isbnError}
          </p>
        )}

        {isbnValidation.isChecking && (
          <p className="text-sm text-warning flex items-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            {t('books.isbn.checkingDuplicate')}
          </p>
        )}

        {isbnValidation.isDuplicate && (
          <p className="text-sm text-error flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {t('books.isbn.duplicateFound', { title: isbnValidation.existingBookTitle })}
          </p>
        )}

        {isbnData && hasAutoFilled && !isbnValidation.isDuplicate && (
          <p className="text-sm text-success flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {t('books.isbn.bookFound')}
          </p>
        )}

        <p className="text-xs text-muted-foreground">{t('books.isbn.enterIsbnHelp')}</p>
      </div>

      {/* Title - Required */}
      <div className="space-y-2">
        <label htmlFor="title" className="text-sm font-medium text-foreground">
          {t('books.fields.title')} *
        </label>
        <Input
          id="title"
          type="text"
          placeholder={t('books.fields.title')}
          {...register('title')}
          className={errors.title ? 'border-error' : ''}
        />
        {errors.title && <p className="text-sm text-error">{errors.title.message}</p>}
      </div>

      {/* Author - Required */}
      <div className="space-y-2">
        <label htmlFor="author" className="text-sm font-medium text-foreground">
          {t('books.fields.author')} *
        </label>
        <Input
          id="author"
          type="text"
          placeholder={t('books.fields.author')}
          {...register('author')}
          className={errors.author ? 'border-error' : ''}
        />
        {errors.author && <p className="text-sm text-error">{errors.author.message}</p>}
      </div>

      {/* ISBN - Hidden field (populated from lookup) */}
      <input type="hidden" {...register('isbn')} />

      {/* Cover URL - Optional */}
      <div className="space-y-2">
        <label htmlFor="cover_url" className="text-sm font-medium text-foreground">
          {t('books.fields.coverUrl')} {t('common.optional')}
        </label>
        <Input
          id="cover_url"
          type="url"
          placeholder="https://example.com/book-cover.jpg"
          {...register('cover_url')}
          className={errors.cover_url ? 'border-error' : ''}
        />
        {errors.cover_url && <p className="text-sm text-error">{errors.cover_url.message}</p>}
        <p className="text-xs text-muted-foreground">{t('books.actions.coverUrlHelp')}</p>
      </div>

      {/* Status - Hidden when hideStatusField is true */}
      {!hideStatusField && (
        <div className="space-y-2">
          <label htmlFor="status" className="text-sm font-medium text-foreground">
            {t('books.fields.status')}
          </label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                id="status"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={[
                  { value: 'unread', label: t('books.status.toRead') },
                  { value: 'reading', label: t('books.status.reading') },
                  { value: 'read', label: t('books.status.read') },
                  { value: 'wishlist', label: t('books.status.wishlist') },
                ]}
              />
            )}
          />
        </div>
      )}

      {/* Type - Physical or Digital */}
      <div className="space-y-2">
        <label htmlFor="type" className="text-sm font-medium text-foreground">
          {t('books.fields.type')}
        </label>
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Select
              id="type"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              options={[
                { value: 'physical', label: t('books.type.physical') },
                { value: 'digital', label: t('books.type.digital') },
              ]}
            />
          )}
        />
      </div>

      {/* Ownership - borrowed books count as reads, not as part of the library */}
      <div className="space-y-2">
        <Controller
          name="ownership"
          control={control}
          render={({ field }) => (
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <input
                type="checkbox"
                checked={field.value === 'borrowed'}
                onChange={(e) => field.onChange(e.target.checked ? 'borrowed' : 'owned')}
                className="h-4 w-4 rounded border-input"
              />
              {t('books.fields.borrowed')}
            </label>
          )}
        />
        {watch('ownership') === 'borrowed' && (
          <Input
            id="borrowed_from"
            maxLength={100}
            placeholder={t('books.fields.borrowedFromPlaceholder')}
            aria-label={t('books.fields.borrowedFrom')}
            {...register('borrowed_from')}
          />
        )}
        <p className="text-xs text-muted-foreground">{t('books.fields.borrowedHelp')}</p>
      </div>

      {/* Genres - Optional */}
      <div className="space-y-2">
        <label htmlFor="genres" className="text-sm font-medium text-foreground">
          {t('books.fields.genres')}
        </label>
        <GenreMultiSelect
          selectedGenres={selectedGenres}
          onGenresChange={setSelectedGenres}
          className="w-full"
        />
        <p className="text-xs text-muted-foreground">
          {t('common.optional')} - {t('books.fields.selectGenresHelp')}
        </p>
      </div>

      {/* Rating - Only show for read books */}
      {currentStatus === 'read' && (
        <div className="space-y-2">
          <label htmlFor="rating" className="text-sm font-medium text-foreground">
            {t('books.fields.rating')}
          </label>
          <Controller
            name="rating"
            control={control}
            render={({ field }) => (
              <Select
                id="rating"
                value={field.value ? String(field.value) : ''}
                onChange={(v) => field.onChange(v === '' ? undefined : Number(v))}
                onBlur={field.onBlur}
                options={[
                  { value: '', label: t('books.actions.noRating') },
                  { value: '1', label: t('books.actions.oneStarRating') },
                  { value: '2', label: t('books.actions.twoStarRating') },
                  { value: '3', label: t('books.actions.threeStarRating') },
                  { value: '4', label: t('books.actions.fourStarRating') },
                  { value: '5', label: t('books.actions.fiveStarRating') },
                ]}
              />
            )}
          />
          <p className="text-xs text-muted-foreground">{t('books.actions.rateExperience')}</p>
        </div>
      )}

      {/* Reading Dates - start for reading/read books, end only for read books */}
      {(currentStatus === 'read' || currentStatus === 'reading') && (
        <>
          {/* Start Date */}
          <div className="space-y-2">
            <label htmlFor="start_date" className="text-sm font-medium text-foreground">
              {t('books.fields.startDate')}
            </label>
            <input
              id="start_date"
              type="date"
              {...register('start_date')}
              className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            />
            <p className="text-xs text-muted-foreground">{t('books.actions.whenStartedReading')}</p>
          </div>

          {/* End Date */}
          {currentStatus === 'read' && (
            <div className="space-y-2">
              <label htmlFor="end_date" className="text-sm font-medium text-foreground">
                {t('books.fields.endDate')}
              </label>
              <input
                id="end_date"
                type="date"
                {...register('end_date')}
                className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
              <p className="text-xs text-muted-foreground">
                {t('books.actions.whenFinishedReading')}
              </p>
            </div>
          )}
        </>
      )}

      {/* Notes - Optional */}
      <div className="space-y-2">
        <label htmlFor="notes" className="text-sm font-medium text-foreground">
          {t('books.fields.notes')}
        </label>
        <textarea
          id="notes"
          placeholder={t('books.actions.addNotesPlaceholder')}
          {...register('notes')}
          rows={4}
          className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-vertical"
        />
      </div>

      {/* Form Actions */}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" disabled={!canSubmit} className="w-full sm:w-auto">
          {isProcessing ? (
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              {t('common.loading')}
            </div>
          ) : (
            t('common.save')
          )}
        </Button>
      </div>

      {/* Barcode Scanner Modal */}
      <BarcodeScanner
        isOpen={isScannerOpen}
        onResult={handleBarcodeResult}
        onError={handleScannerError}
        onClose={() => setIsScannerOpen(false)}
      />
    </form>
  )
}
